import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BroadcastItem, BroadcastListBody, BroadcastMedia, BroadcastReadFilter, ParentBroadcastService } from './parent-broadcast.service';

const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;

// Broadcast — V2 list (BrodcastListView.js): filter pills (All/Unread with
// live count), debounced search, pagination, and a detail "sheet" (a
// bottom-anchored modal here, standing in for RBSheet — see
// parent-broadcast.component.html) opened per-card. Tapping an unread card
// fires markBroadcastRead fire-and-forget and updates the list
// optimistically, exactly per the API reference this was built from — no
// manual "mark as read" action exists.
@Component({
  selector: 'app-parent-broadcast',
  templateUrl: './parent-broadcast.component.html',
  styleUrls: ['./parent-broadcast.component.css'],
})
export class ParentBroadcastComponent implements OnInit, OnChanges {

  @Input() academicId = '';
  @Input() studentSwitchTick = 0;

  items: BroadcastItem[] = [];
  unreadCount = 0;
  currentPage = 1;
  maxPageNumber = 1;
  loading = false;
  error = '';

  activeFilter: BroadcastReadFilter = 'ALL';
  searchTerm = '';
  private searchDebounceTimer: any;

  selectedItem: BroadcastItem | null = null;

  // In-app attachment preview — same small-window pattern as diary/
  // leave-request's own media viewers, instead of opening a new browser
  // tab. Field names (broadcastMediaType/broadcastMediaUrl) confirmed
  // against broadcast-v2.component.ts's mapBroadcastDetail, which reads the
  // same broadcastMediaList shape from the admin-side broadcast endpoint.
  imageViewerUrl: string | null = null;
  pdfViewerUrl: SafeResourceUrl | null = null;

  private requestId = 0;

  constructor(private broadcastService: ParentBroadcastService, private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this.loadList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentSwitchTick'] && !changes['studentSwitchTick'].firstChange) {
      this.activeFilter = 'ALL';
      this.searchTerm = '';
      this.currentPage = 1;
      this.selectedItem = null;
      this.loadList();
    }
  }

  setFilter(filter: BroadcastReadFilter): void {
    if (this.activeFilter === filter) { return; }
    this.activeFilter = filter;
    this.currentPage = 1;
    this.loadList();
  }

  onSearchInput(value: string): void {
    this.searchTerm = value;
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this.loadList();
    }, SEARCH_DEBOUNCE_MS);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.maxPageNumber || page === this.currentPage) { return; }
    this.currentPage = page;
    this.loadList();
  }

  goToPrevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  loadList(): void {
    this.loading = true;
    this.error = '';
    const requestId = ++this.requestId;
    this.broadcastService.getBroadcastMessages(this.academicId, this.activeFilter, this.searchTerm, this.currentPage, PAGE_LIMIT).subscribe({
      next: (res: any) => {
        if (requestId !== this.requestId) { return; }
        this.loading = false;
        if (res && res.status && res.body) {
          const body: BroadcastListBody = res.body;
          this.items = body.broadcastDetail || [];
          this.maxPageNumber = body.maxPageNumber || 1;
          this.unreadCount = body.unreadCount || 0;
        } else {
          this.items = [];
          this.maxPageNumber = 1;
          this.error = (res && res.message) || 'Unable to load broadcasts.';
        }
      },
      error: (err) => {
        if (requestId !== this.requestId) { return; }
        this.loading = false;
        this.items = [];
        this.error = (err && err.error && err.error.message) || 'Unable to load broadcasts.';
      },
    });
  }

  trackByBroadcastId(_: number, item: BroadcastItem): number {
    return item.broadcastId;
  }

  openDetail(item: BroadcastItem): void {
    this.selectedItem = item;
    if (!item.isRead) {
      this.markRead(item);
    }
  }

  closeDetail(): void {
    this.selectedItem = null;
  }

  // ---------- Attachment preview ----------

  mediaUrl(media: BroadcastMedia): string {
    return media?.broadcastMediaUrl || '';
  }

  isImageMedia(media: BroadcastMedia): boolean {
    return media?.broadcastMediaType === 'IMAGE';
  }

  openAttachment(media: BroadcastMedia): void {
    const url = this.mediaUrl(media);
    if (!url) { return; }
    if (this.isImageMedia(media)) {
      this.imageViewerUrl = url;
    } else {
      this.pdfViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
  }

  closeImageViewer(): void {
    this.imageViewerUrl = null;
  }

  closePdfViewer(): void {
    this.pdfViewerUrl = null;
  }

  // Fire-and-forget, not awaited — same as the mobile app. UI updates
  // optimistically regardless of the actual response (per the API
  // reference: "The response is never even checked").
  private markRead(item: BroadcastItem): void {
    this.broadcastService.markBroadcastRead(item.broadcastId).subscribe();
    this.unreadCount = Math.max(0, this.unreadCount - 1);
    if (this.activeFilter === 'UNREAD') {
      this.items = this.items.filter(i => i.broadcastId !== item.broadcastId);
    } else {
      item.isRead = true;
    }
  }
}
